import { Clock, DollarSign, MapPin, MoreHorizontal, Phone, Mail, User, Eye, Edit2, Trash2, ChevronUp, ChevronDown } from "lucide-react"
import { Badge } from "../ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import moment from "moment"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { updateLead } from "../../api/lead"
import toast from "react-hot-toast"
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd"

// Pipeline columns configuration
const pipelineColumns = [
  {
    id: "hot",
    title: "Hot Leads",
    badgeColor: "bg-red-500",
    stages: ["New Lead", "Attempting Contact", "Booked Appraisal"],
    priority: "high"
  },
  {
    id: "warm",
    title: "Warm Leads",
    badgeColor: "bg-orange-500",
    stages: ["Contacted Nurture", "Follow-Up Ongoing", "Appraisal Complete - Awaiting Decision"],
    priority: "medium"
  },
  {
    id: "cold",
    title: "Cold Leads",
    badgeColor: "bg-blue-500",
    stages: ["Contact - Not Qualified", "Unresponsive - Cold", "Lost - Listed With Another Agent"],
    priority: "low"
  },
  {
    id: "unassessed",
    title: "Unassessed Leads",
    badgeColor: "bg-gray-500",
    stages: ["Unassessed"],
    priority: "unassigned"
  },
];

export function PipelineBoard({ leads, totalLeads, onLeadClick, onLeadEdit, onLeadDelete }) {
  const navigate = useNavigate();
  const [updatingLeadId, setUpdatingLeadId] = useState(null);

  // Handle priority change
  const handlePriorityChange = async (lead, newPriority) => {
    try {
      setUpdatingLeadId(lead.id);

      await updateLead(lead.id, { priority: newPriority });

      toast.success(`Lead priority changed to ${newPriority.toUpperCase()}`);

      // Trigger a refresh by calling the parent's edit handler which should refetch
      if (onLeadEdit) {
        // Refresh the page or trigger a refetch
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating lead priority:', error);
      toast.error('Failed to update lead priority');
    } finally {
      setUpdatingLeadId(null);
    }
  };

  // Handle lead card click
  const handleLeadClick = (lead, e) => {
    // Prevent card click when clicking action buttons
    if (e.target.closest('.action-button')) {
      return;
    }
    if (onLeadClick) {
      onLeadClick(lead);
    }
  };

  // Group leads by pipeline column
  const getLeadsForColumn = (column) => {
    if (!leads || leads.length === 0) return [];

    const columnLeads = leads.filter(lead => {
      // Check if lead stage matches column stages
      const stageMatch = column.stages.includes(lead.stage);

      // Check if lead priority matches column priority (handle both capitalized and lowercase)
      const leadPriority = lead.priority?.toLowerCase() || 'medium';
      const columnPriority = column.priority?.toLowerCase() || 'medium';
      const priorityMatch = leadPriority === columnPriority;

      return stageMatch || priorityMatch;
    });

    return columnLeads;
  };

  // Calculate column totals
  const getColumnTotals = (columnLeads) => {
    if (!columnLeads || columnLeads.length === 0) return { count: 0, totalValue: 0 };

    const totalValue = columnLeads.reduce((sum, lead) => {
      // Try to extract value from price_range_text
      let value = 0;
      if (lead.price_range_text) {
        const numbers = lead.price_range_text.match(/\d+/g);
        if (numbers && numbers.length > 0) {
          const avgPrice = numbers.reduce((sum, num) => sum + parseInt(num), 0) / numbers.length;
          value = avgPrice;
        }
      }
      return sum + value;
    }, 0);

    return {
      count: columnLeads.length,
      totalValue: totalValue
    };
  };

  // Format lead data for display
  const formatLeadData = (lead) => {
    return {
      id: lead.id,
      name: lead.full_name || lead.first_name + ' ' + lead.last_name || 'Unknown',
      property: lead.property_address || 'No address',
      value: (() => {
        // Try to extract value from price_range_text
        if (lead.price_range_text) {
          const numbers = lead.price_range_text.match(/\d+/g);
          if (numbers && numbers.length > 0) {
            const avgPrice = numbers.reduce((sum, num) => sum + parseInt(num), 0) / numbers.length;
            return avgPrice;
          }
        }
        return 0;
      })(),
      source: lead.source || 'Unknown',
      lastContact: lead.updated_at ? moment(lead.updated_at).fromNow() : 'Never',
      phone: lead.contact || 'No phone',
      email: lead.email || 'No email',
      notes: lead.initial_notes || 'No notes',
      stage: lead.stage || 'Unknown',
      priority: lead.priority?.toLowerCase() || 'medium',
      commission: parseFloat(lead.commission || 0),
      gainedCommission: parseFloat(lead.gained_commission || 0)
    };
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const leadId = parseInt(draggableId);
    const targetColumnId = destination.droppableId;
    const targetColumn = pipelineColumns.find(c => c.id === targetColumnId);

    if (!targetColumn) return;

    const newPriority = targetColumn.priority;
    const lead = leads.find(l => l.id === leadId);

    if (!lead) return;

    try {
      setUpdatingLeadId(leadId);
      await updateLead(leadId, { priority: newPriority });
      toast.success(`Lead moved to ${targetColumn.title}`);

      // For now we use the same reload pattern as handlePriorityChange
      // to ensure consistent behavior with the parent component
      window.location.reload();

    } catch (error) {
      console.error('Error updating lead priority via drag:', error);
      toast.error('Failed to update lead priority');
    } finally {
      setUpdatingLeadId(null);
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="space-y-4">
        {/* Scroll hint */}
        <div className="flex items-center justify-between text-sm text-gray-500 px-1">
          <span className="font-medium">Scroll horizontally to view all columns →</span>
          <span className="text-xs bg-gray-100 px-2 py-1 rounded">
            {leads.length} leads across {pipelineColumns.length} stages
          </span>
        </div>

        {/* Pipeline Board - Horizontal Scroll */}
        <div className="overflow-x-auto pb-6 -mx-2 px-2">
          <div className="flex gap-6 min-w-max pb-2">
            {pipelineColumns.map((column) => {
              const columnLeads = getLeadsForColumn(column);
              const totals = getColumnTotals(columnLeads);

              return (
                <Card key={column.id} className="border border-gray-200 shadow-sm bg-white w-80 flex-shrink-0">
                  <CardHeader className="pb-3 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${column.badgeColor}`}></div>
                        <CardTitle className="text-lg font-semibold text-gray-900">
                          {column.title}
                        </CardTitle>
                      </div>
                      <Badge className={`${column.badgeColor} text-white border-0`}>
                        {totals.count}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <DollarSign className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">
                        ${totals.totalValue.toLocaleString()}
                      </span>
                    </div>
                  </CardHeader>

                  <Droppable droppableId={column.id}>
                    {(provided, snapshot) => (
                      <CardContent
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={`space-y-3 pt-4 max-h-[calc(100vh-350px)] overflow-y-auto px-3 transition-colors ${snapshot.isDraggingOver ? 'bg-gray-50/80' : ''}`}
                      >
                        {columnLeads.length > 0 ? (
                          columnLeads.map((lead, index) => {
                            const formattedLead = formatLeadData(lead);

                            return (
                              <Draggable key={formattedLead.id} draggableId={formattedLead.id.toString()} index={index}>
                                {(provided, snapshot) => (
                                  <Card
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`bg-white border border-gray-200 hover:border-[#55C5D0] hover:shadow-md transition-all cursor-pointer group ${snapshot.isDragging ? 'shadow-xl z-50' : ''}`}
                                    onClick={(e) => handleLeadClick(lead, e)}
                                  >
                                    <CardContent className="p-3">
                                      <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                          <h4 className="font-semibold text-gray-900 mb-2 text-sm">{formattedLead.name}</h4>
                                          <div className="flex items-start gap-1.5 text-xs text-gray-600">
                                            <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                            <span className="line-clamp-2 leading-relaxed">{formattedLead.property}</span>
                                          </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (onLeadClick) onLeadClick(lead);
                                            }}
                                            className="action-button p-1.5 rounded-md hover:bg-blue-50 text-gray-500 hover:text-blue-600 transition-colors"
                                            title="View Details"
                                          >
                                            <Eye className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (onLeadEdit) onLeadEdit(lead);
                                            }}
                                            className="action-button p-1.5 rounded-md hover:bg-green-50 text-gray-500 hover:text-green-600 transition-colors"
                                            title="Edit Lead"
                                          >
                                            <Edit2 className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (onLeadDelete) onLeadDelete(lead);
                                            }}
                                            className="action-button p-1.5 rounded-md hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors"
                                            title="Delete Lead"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </div>

                                      {/* Priority Change Buttons */}
                                      <div className="mb-3 flex items-center justify-between bg-gray-50 p-2 rounded">
                                        <span className="text-xs font-medium text-gray-600">Priority:</span>
                                        <div className="flex items-center gap-1">
                                          {formattedLead.priority !== 'high' && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePriorityChange(lead, 'high');
                                              }}
                                              disabled={updatingLeadId === lead.id}
                                              className="action-button px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
                                              title="Change to Hot"
                                            >
                                              Hot
                                            </button>
                                          )}
                                          {formattedLead.priority !== 'medium' && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePriorityChange(lead, 'medium');
                                              }}
                                              disabled={updatingLeadId === lead.id}
                                              className="action-button px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors disabled:opacity-50"
                                              title="Change to Warm"
                                            >
                                              Warm
                                            </button>
                                          )}
                                          {formattedLead.priority !== 'low' && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePriorityChange(lead, 'low');
                                              }}
                                              disabled={updatingLeadId === lead.id}
                                              className="action-button px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors disabled:opacity-50"
                                              title="Change to Cold"
                                            >
                                              Cold
                                            </button>
                                          )}
                                          {updatingLeadId === lead.id && (
                                            <span className="text-xs text-gray-500">Updating...</span>
                                          )}
                                        </div>
                                      </div>

                                      {(formattedLead.commission > 0 || formattedLead.gainedCommission > 0) && (
                                        <div className="space-y-1 mb-3 bg-gradient-to-r from-blue-50 to-green-50 p-2 rounded">
                                          {formattedLead.commission > 0 && (
                                            <div className="flex items-center justify-between text-xs">
                                              <span className="text-gray-600">Expected:</span>
                                              <span className="font-bold text-blue-600">
                                                ${formattedLead.commission.toLocaleString()}
                                              </span>
                                            </div>
                                          )}
                                          {formattedLead.gainedCommission > 0 && (
                                            <div className="flex items-center justify-between text-xs">
                                              <span className="text-gray-600">Gained:</span>
                                              <span className="font-bold text-green-600">
                                                ${formattedLead.gainedCommission.toLocaleString()}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      <div className="flex flex-wrap gap-1.5 mb-3">
                                        <Badge variant="outline" className="text-xs border-gray-300 text-gray-700">
                                          {formattedLead.source}
                                        </Badge>
                                        <Badge variant="outline" className="text-xs border-gray-300 text-gray-700">
                                          {formattedLead.stage}
                                        </Badge>
                                      </div>

                                      {formattedLead.notes && formattedLead.notes !== 'No notes' && (
                                        <p className="text-xs text-gray-700 mb-3 line-clamp-3 bg-amber-50 p-2.5 rounded border-l-2 border-amber-300 leading-relaxed">
                                          {formattedLead.notes}
                                        </p>
                                      )}

                                      <div className="flex items-center text-xs text-gray-500 pt-2.5 border-t border-gray-100">
                                        <Clock className="w-3.5 h-3.5 mr-1.5" />
                                        <span className="font-medium">{formattedLead.lastContact}</span>
                                      </div>
                                    </CardContent>
                                  </Card>
                                )}
                              </Draggable>
                            );
                          })
                        ) : (
                          <div className="text-center py-12 text-gray-400">
                            <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm">No leads</p>
                          </div>
                        )}
                        {provided.placeholder}
                      </CardContent>
                    )}
                  </Droppable>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </DragDropContext>
  )
}
